import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import type { AffiliateRecord } from "@itaas/pep-contracts";
import { AFFILIATE_PROFILE_LABELS } from "@itaas/pep-contracts";
import {
  fetchAffiliatesAdmin,
  recordAffiliatePayout,
  updateAffiliateStatus,
} from "../api/affiliate-api";

export function AdminAffiliatesPage() {
  const [rows, setRows] = useState<AffiliateRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function reload() {
    fetchAffiliatesAdmin()
      .then(setRows)
      .catch(() => setError("Impossible de charger les ambassadeurs."));
  }

  useEffect(() => {
    reload();
  }, []);

  async function toggleSuspend(a: AffiliateRecord) {
    const next = a.status === "active" ? "suspended" : "active";
    let reason: string | undefined;
    if (next === "suspended") {
      reason =
        window.prompt("Motif de suspension (fraude, inactivité, litige…) :") ?? undefined;
      if (reason === undefined) return;
    }
    setBusyId(a.id);
    setError(null);
    try {
      const updated = await updateAffiliateStatus(a.id, next, reason);
      setRows((list) => list.map((r) => (r.id === a.id ? updated : r)));
    } catch {
      setError("Échec mise à jour du statut.");
    } finally {
      setBusyId(null);
    }
  }

  async function onPayout(a: AffiliateRecord) {
    if (!window.confirm(`Marquer comme payé $${(a.pendingCashUsd ?? 0).toFixed(2)} à ${a.name} ?`)) {
      return;
    }
    setBusyId(a.id);
    setError(null);
    try {
      const updated = await recordAffiliatePayout(a.id, "Versement admin");
      setRows((list) => list.map((r) => (r.id === a.id ? updated : r)));
    } catch {
      setError("Échec enregistrement paiement.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <h1 className="h3 fw-bold mb-3">Ambassadeurs & affiliation</h1>
      <p className="text-secondary small mb-4">
        Suspendez un ambassadeur défaillant (fraude, spam) — ses liens ne génèrent plus de leads.
        Versez les commissions cash quand le seuil est atteint.
      </p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="table-responsive">
        <table className="table table-sm align-middle bg-body rounded">
          <thead>
            <tr>
              <th>Code</th>
              <th>Nom</th>
              <th>Parrain</th>
              <th>Statut</th>
              <th>Profil</th>
              <th>Produits</th>
              <th>Leads</th>
              <th>Conv.</th>
              <th>Recr.</th>
              <th>Commissions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className={a.status === "suspended" ? "table-secondary" : undefined}>
                <td className="font-monospace fw-semibold">{a.code}</td>
                <td>
                  {a.name}
                  {a.organization && (
                    <div className="small text-secondary">{a.organization}</div>
                  )}
                  <div className="small text-secondary">{a.phone}</div>
                </td>
                <td className="small font-monospace">
                  {a.parentAffiliateCode ?? "—"}
                </td>
                <td>
                  {a.status === "active" ? (
                    <span className="badge text-bg-success">Actif</span>
                  ) : (
                    <span className="badge text-bg-danger" title={a.suspensionReason}>
                      Suspendu
                    </span>
                  )}
                </td>
                <td className="small">{AFFILIATE_PROFILE_LABELS[a.profileType] ?? a.profileType}</td>
                <td className="small">{a.products.join(", ")}</td>
                <td>{a.referralCount}</td>
                <td>{a.conversionCount}</td>
                <td>{a.recruitCount ?? 0}</td>
                <td className="small">
                  {a.rewardType === "cash_commission" ? (
                    <>
                      <div>
                        <strong className="text-warning">${(a.pendingCashUsd ?? 0).toFixed(2)}</strong>{" "}
                        dû
                      </div>
                      <div className="text-secondary">
                        Payé : ${(a.paidCashUsd ?? 0).toFixed(2)}
                      </div>
                    </>
                  ) : (
                    <div>
                      {a.pendingInvoiceDiscounts ?? 0} réduc. facture en attente
                      {a.invoiceDiscountPercent != null && (
                        <div className="text-secondary">−{a.invoiceDiscountPercent}%</div>
                      )}
                    </div>
                  )}
                </td>
                <td className="small">
                  <div className="d-flex flex-column gap-1">
                    <Link
                      to={`/admin/leads?affiliate=${encodeURIComponent(a.code)}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      Voir filleuls
                    </Link>
                    <button
                      type="button"
                      className={`btn btn-sm ${a.status === "active" ? "btn-outline-danger" : "btn-outline-success"}`}
                      disabled={busyId === a.id}
                      onClick={() => void toggleSuspend(a)}
                    >
                      {a.status === "active" ? "Suspendre" : "Réactiver"}
                    </button>
                    {a.rewardType === "cash_commission" && (a.pendingCashUsd ?? 0) > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        disabled={busyId === a.id}
                        onClick={() => void onPayout(a)}
                      >
                        Marquer payé
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && !error && (
              <tr>
                <td colSpan={11} className="text-secondary text-center py-4">
                  Aucun ambassadeur inscrit.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
